/* Utils */

theme.shop.currency_extra_formats = undefined;
/**
 * Formats a number using the cart currency
 *
 * @param {number} cents Amount in cents
 * @param {string} [round="ceil | floor | round"]
 * @return {string} The formated number in the cart currency
 */
const formatMoney = function (
  cents,
  round = "round",
  currency = null,
  removeSpace = false,
) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  let formatString = theme.shop.money_format;
  if (currency !== null) {
    let currencySymbol;
    if (
      theme.cart.currency_format_object[currency] !== undefined &&
      theme.cart.currency_format_object[currency] !== null
    ) {
      currencySymbol = theme.cart.currency_format_object[currency];
    } else {
      currencySymbol = currency;
    }
    formatString = `${currencySymbol} {{amount}}`;
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return "0";
    }
    number = number / 100;

    switch (round) {
      case "ceil":
        number = Math.ceil(number);
        break;
      case "floor":
        number = Math.floor(number);
        break;
      default:
        number = number.toFixed(precision);
    }

    const parts = String(number).split(".");
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands,
    );
    const cents = parts[1] ? decimal + parts[1] : "";
    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  if (removeSpace) {
    formatString = formatString.replace(/\s/gi, "");
  }
  return formatString.replace(placeholderRegex, value).toUpperCase();
};

const logger = {
  info: (message) => {
    window.Sentry && window.Sentry.captureMessage(message);
  },
  error: (err) => {
    window.Sentry && window.Sentry.captureException(err);
  },
};

/**
 * Formats a date into a short version of it
 *
 * @param {Date} date Date to format
 * @param {string} delimiter delimiter for format default: /
 * @return {string} formated date  M / D / Y
 */
function formatDateShort(date, delimiter = "/") {
  const dateObject = new Date(date);
  const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(
    dateObject,
  );
  const month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(
    dateObject,
  );
  const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(
    dateObject,
  );
  return `${month}${delimiter}${day}${delimiter}${year}`;
}
function getDateTranslated(dateString = "") {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(
    `${theme.shop.language}-${theme.cart.country_code}`,
    {
      day: "numeric",
      month: "long",
      weekday: "long",
      year: "numeric",
    },
  ).format(date);
}
/**
 * @param {htmlElement} htmlElement
 * @param {{}} replaceData key value to replace
 */
function replaceMultipleInnerText(htmlElement, replaceData = {}) {
  // accepts array of objects with key being replacement value and value the replaced values
  if (!htmlElement || !replaceData || !Object.entries(replaceData).length)
    return;
  Object.entries(replaceData).forEach(([key, value]) => {
    htmlElement.innerHTML = htmlElement.innerHTML.replaceAll([key], [value]);
  });
}

/**
 * Asynchronously load a script
 * @param {string} src
 * @param {string} type
 * @returns Promise
 */
window.loadedScripts = {};
const loadScript = (src, type = "text/javascript") => {
  const head = document.querySelector("head");
  if (!window.loadedScripts[src]) {
    window.loadedScripts[src] = new Promise((r) => {
      let l = false;
      const script = document.createElement("script");
      script.type = type;
      script.src = src;
      script.crossorigin = "anonymous";
      script.async = true;
      script.onload = script.onreadystatechange = function () {
        if (!l && (!this.readyState || this.readyState === "complete")) {
          l = true;
          r();
        }
      };
      head.appendChild(script);
    });
  }
  return window.loadedScripts[src];
};

/**
 * Returns the path after adding the new query param to it
 * @param path — The current path
 * @param key — The query param key
 * @param value — The query param value
 */
function addQueryParam(path = "", key = "", value = "") {
  let finalPath = path;
  if (finalPath.indexOf("?") !== -1) {
    finalPath = finalPath + `&${key}=${value}`;
  } else {
    finalPath = finalPath + `?${key}=${value}`;
  }
  return finalPath;
}

function parseSearchParams(search) {
  if (!search) {
    return {};
  }

  return search
    .replace(/^\?/, "")
    .split("&")
    .map((param) => param.split("="))
    .reduce(
      (acc, [k, v]) => ({
        ...acc,
        [window.decodeURIComponent(k)]: window.decodeURIComponent(v),
      }),
      {},
    );
}

const apiFetch = (BASE_URL, path, opts) => {
  return fetch(`${BASE_URL}${path}`, opts);
};

const apiFetchJson = async (BASE_URL, path, opts) => {
  const req = await fetch(`${BASE_URL}${path}`, opts);
  return req.json();
};

/**
 * Fetches text from an API endpoint
 *
 * @param {string} BASE_URL - The base URL of the API
 * @param {string} path - The endpoint path (e.g. "/cart")
 * @param {RequestInit} [opts] - Optional fetch configuration
 * @returns {Promise<string>} The response body as text
 */

const apiFetchText = async (BASE_URL, path, opts) => {
  const req = await fetch(`${BASE_URL}${path}`, opts);
  return req.text();
};

const apiFetchStorefront = async (body, method = "POST") => {
  return apiFetchJson("", "/api/2025-04/graphql.json", {
    headers: {
      "x-shopify-storefront-access-token":
        theme.settings.storefront_access_token,
      "Content-Type": "application/json",
    },
    method,
    body,
  });
};

const apiFetchProxy = async (path, headers) => {
  return apiFetchJson(
    theme.settings.pmall_brand_services_proxy_url,
    path,
    headers,
  );
};

/**
 * This function should not be used directly. Prefer one of the more specific
 * functions below.
 *
 * @param data {[key: string] :  any } - The data to push into the datalayer. e.g
 * @returns {Promoise<void>}
 */
const layerPush = (data) => {
  const c = getCookie("disable_analytics");
  if (c) return;
  return new Promise((resolve) => {
    data.codeSource = theme.settings.analytics_code_source;
    data.exchangeRate = theme.cart.currency_with_vat;

    const currentCallback = data.eventCallback;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign({}, data, {
        eventCallback: () => {
          if (typeof currentCallback === "function") {
            currentCallback();
          }
          resolve();
        },
      }),
    );

    if (!window.google_tag_manager) {
      if (typeof currentCallback === "function") {
        currentCallback();
      }
      resolve();
    }

    // Send all events to Pangaea Analytics
    window._pgAnalytics = window._pgAnalytics || [];
    window._pgAnalytics.push(data);
  });
};

const trackAmazonCollectionCTA = (amazonProductTitle) => {
  return layerPush({
    event: "addToCartAmazon",
    amazonProductTitle,
  });
};

/**
 * This event is triggered each time we visit a page.
 * @param {PageView} pageData Refer to the PageView defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackPageView = (pageData) => {
  return layerPush({
    event: "pageView",
    page: pageData,
  });
};

/**
 * Push an event or user data to Optimizely
 * @param {object} data
 */
const optimizelyPush = (data) => {
  window["optimizely"] = window["optimizely"] || [];
  window["optimizely"].push(data);
};

const isAmplitudeAvailable = () => {
  return window.amplitude && window.amplitude.getDeviceId();
};

const getDeviceId = () => {
  return isAmplitudeAvailable() ? window.amplitude.getDeviceId() : getUUID();
};

const triggerExperiment = (experimentKey) => {
  if (isAmplitudeAvailable() && window.growthbookInstance) {
    window.growthbookInstance.triggerExperiment(experimentKey);
  }
};

/**
 * This event is triggered each time we visit a page.
 * @param {EcommerceImpression} impressions Refer to the EcommerceImpression defination in the DATATYPES above
 * @param {currencyCode} string  The cart currency
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductImpression = (impressions, currencyCode) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceImpression",
    ecommerce: { impressions, currencyCode },
  });
};

/**
 * This event is triggered each time we click on a product link that takes us to a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductClick = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceProductClick",
    ecommerce: {
      click: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time we visit a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductDetail = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceDetailPage",
    ecommerce: {
      detail: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time the user state is updated
 * @param {Object} traits
 */
const trackUserTraits = (traits) => {
  layerPush({
    event: "identifiedUserTraits",
    traits,
  });
};

const showLoader = (show = true) => {
  document.body.classList.add("overflow-hidden");
  const modalLoader = document.querySelector("#modalPreloader");
  if (show) {
    return modalLoader.classList.add("loading", "d-flex");
  }
  modalLoader.classList.remove("loading", "d-flex");
  document.body.classList.remove("overflow-hidden");
};

const openModal = (eventSource) => {
  const modalSelector = eventSource.getAttribute("data-target");
  const modalSectionId = eventSource.getAttribute("data-target-section-id");
  const modalScriptUrl = eventSource.getAttribute("data-target-script-url");

  const fetchModal = () => {
    document.body.classList.add("overflow-hidden");
    showLoader();
    apiFetchText("", `?section_id=${modalSectionId}`)
      .then((response) => {
        document.querySelector("#modal-container").innerHTML = response;
        const returnInput = document.querySelector('input[name="return_to"]');
        if (returnInput) returnInput.value = returnInput.value.split("?")[0];
        const modalScript = document.createElement("script");
        modalScript.src = modalScriptUrl;
        document.head.append(modalScript);
      })
      .finally(() => {
        showLoader(false);
      });
  };

  const modal = document.querySelector(modalSelector);
  if (modal) {
    return modal.showModal();
  }
  fetchModal();
};

if (!customElements.get("convert-to-cart-currency")) {
  customElements.define(
    "convert-to-cart-currency",
    class ConvertToCartCurrency extends HTMLElement {
      constructor() {
        super();
      }
      connectedCallback() {
        const roundingMethod = this.getAttribute("data-round");
        const value = this.getAttribute("data-value");
        const suffix = this.getAttribute("data-suffix") || "";

        let rate = theme.cart.currency_with_vat;
        const externalRate = theme.shop.external_exchange_rate;
        if (
          this.getAttribute("data-rate") === "external" &&
          Shopify &&
          Shopify.currency &&
          Shopify.currency.rate &&
          externalRate
        ) {
          // Use VAT from currency-convert product
          rate = (rate / Shopify.currency.rate) * externalRate;
        }
        const amount = formatMoney(
          Number(value) * Number(rate),
          roundingMethod,
        );
        this.innerHTML = `${amount}${suffix}`;
      }
    },
  );
}
/**
 * use to async await some time
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 * @param ms: milliseconds to wait until resolve
 */
function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
const mobileQuery = window.matchMedia("(max-width: 991px)");
const isMobile = () => mobileQuery.matches;

// Important Utility Functions
async function updateCart(payload, action = "update") {
  return apiFetchJson(window.Shopify.routes.root, `cart/${action}.js`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
}

function parseJSON(jsonStr = "", fallback = {}) {
  try {
    return JSON.parse(jsonStr);
  } catch (error) {
    return fallback;
  }
}

function isEmpty(value) {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === "" || value === null || value === undefined;
}

function cleanObject(object = {}) {
  const cleanedObject = {};
  Object.entries(object).forEach(([key, value]) => {
    if (!isEmpty(value)) {
      cleanedObject[key] = value;
    }
  });
  return cleanedObject;
}

window.addEventListener("load", () => {
  document.body.classList.add("loaded");
});
function isFacebookBrowser() {
  return /FBAN|FBAV/.test(navigator.userAgent);
}
window.addEventListener("load", () => {
  if (isFacebookBrowser()) {
    var style = document.createElement("style");
    style.innerHTML = `
        html, body {
            height: 100vh !important;
            overflow-y: auto !important;
            scroll-snap-type: none !important;
            scroll-behavior: auto !important;
        }
    `;
  }
});

document.fonts.addEventListener("loadingdone", () => {
  document.body.classList.add("font-loaded");
});

class LazySection extends HTMLElement {
  constructor() {
    super();
  }
  cleanUp = () => {
    this.itemsToRemove.forEach((item) => item.remove());
  };

  async load() {
    const requestPath = this.getAttribute("data-request-path");
    const sectionId = this.getAttribute("data-section-id");
    const targetSelector = this.getAttribute("data-target");
    const removeSelector = this.getAttribute("data-remove-target");

    this.itemsToRemove = [...document.querySelectorAll(removeSelector), this];

    if (!(requestPath && sectionId)) {
      this.cleanUp();
      return;
    }

    try {
      const url = new URL(requestPath, location.origin);
      url.searchParams.set("section_id", sectionId);

      const sectionContent = await apiFetchText("", url.toString());
      const domParser = new DOMParser();
      const parsedDoc = domParser.parseFromString(sectionContent, "text/html");

      let content = [parsedDoc.body.firstElementChild];
      if (targetSelector) {
        content = parsedDoc.querySelectorAll(targetSelector);
      }

      this.replaceWith(...content);
    } catch (error) {
      logger.error("LazySection failed:" + error);
    }
    this.cleanUp();
  }

  connectedCallback() {
    this.load();
  }
}

customElements.define("lazy-section", LazySection);
